import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Save, Calendar, User, FileText } from 'lucide-react';
import supabase from '../../supabase';
import './EditQuotePopup.css';

const EditQuotePopup = ({ 
  quote, 
  isOpen, 
  onClose, 
  onSave,
  clients 
}) => {
  const [formData, setFormData] = useState({
    quotation_number: '',
    issue_date: '',
    discount: 0,
    gst: 0,
    i_gst: 0,
    terms_conditions: [],
    client_id: ''
  });
  
  const [items, setItems] = useState([{
    id: Date.now(),
    p_name: '',
    description: '',
    quantity: 1,
    unit_price: 0
  }]);
  
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize form data when quote changes
  useEffect(() => {
    if (quote && isOpen) {
      fetchQuoteData();
    }
  }, [quote, isOpen]);

  const fetchQuoteData = async () => {
    if (!quote) return;
    
    setLoading(true);
    try {
      // Fetch clients data
      const { data: clientsData } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', quote.user_id);

      // Fetch quote items from the new quote_items table
      const { data: quoteItemsData, error: itemsError } = await supabase
        .from('quote_items')
        .select('*')
        .eq('quote_id', quote.id)
        .order('created_at', { ascending: true });

      if (itemsError) {
        console.error('Error fetching quote items:', itemsError);
      }

      // Parse terms_conditions properly
      let termsArray = [''];
      if (quote.terms_conditions) {
        if (typeof quote.terms_conditions === 'string') {
          // If it's a pipe-separated string
          termsArray = quote.terms_conditions.split('|').filter(term => term.trim());
          if (termsArray.length === 0) termsArray = [''];
        } else if (Array.isArray(quote.terms_conditions)) {
          // If it's already an array
          termsArray = quote.terms_conditions.filter(term => term && term.trim());
          if (termsArray.length === 0) termsArray = [''];
        }
      }

      // Set form data
      setFormData({
        quotation_number: quote.quotation_number || '',
        issue_date: quote.created_at ? quote.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        discount: Number(quote.discount) || 0,
        gst: Number(quote.gst) || 0,
        i_gst: Number(quote.i_gst) || 0,
        terms_conditions: termsArray,
        client_id: quote.client_id || ''
      });

      // Set items from quote_items table
      if (quoteItemsData && quoteItemsData.length > 0) {
        const formattedItems = quoteItemsData.map((item, index) => ({
          id: item.id || Date.now() + index,
          p_name: item.p_name || '',
          description: item.description || '',
          quantity: Number(item.quantity) || 1,
          unit_price: Number(item.unit_price) || 0,
          total_value: Number(item.total_value) || 0,
          isExisting: true
        }));
        setItems(formattedItems);
      } else {
        setItems([{
          id: Date.now(),
          p_name: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          isExisting: false
        }]);
      }

      setClientsList(clientsData || []);

    } catch (error) {
      console.error('Error fetching quote data:', error);
      alert('Error loading quote data');
    }
    setLoading(false);
  };

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Item management functions
  const addItem = useCallback(() => {
    setItems(prev => [...prev, {
      id: Date.now(),
      p_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      isExisting: false
    }]);
  }, []);

  const removeItem = useCallback((itemId) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  }, [items.length]);

  const handleItemChange = useCallback((itemId, field, value) => {
    setItems(prev => prev.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            [field]: field === 'quantity' ? parseInt(value) || 0 : 
                     field === 'unit_price' ? parseFloat(value) || 0 : value,
            // Update total_value when quantity or unit_price changes
            total_value: field === 'quantity' || field === 'unit_price' ? 
              ((field === 'quantity' ? (parseInt(value) || 0) : item.quantity) * 
               (field === 'unit_price' ? (parseFloat(value) || 0) : item.unit_price)) : 
              item.total_value
          }
        : item
    ));
  }, []);

  // Terms & Conditions functions
  const addTerm = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      terms_conditions: [...prev.terms_conditions, '']
    }));
  }, []);

  const removeTerm = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      terms_conditions: prev.terms_conditions.filter((_, i) => i !== index)
    }));
  }, []);

  const handleTermChange = useCallback((index, value) => {
    setFormData(prev => ({
      ...prev,
      terms_conditions: prev.terms_conditions.map((term, i) => i === index ? value : term)
    }));
  }, []);

  // Calculation functions
  const calculateItemTotal = (item) => {
    return (item.quantity || 0) * (item.unit_price || 0);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateDiscountAmount = () => {
    return calculateSubtotal() * ((formData.discount || 0) / 100);
  };

  const calculateAfterDiscount = () => {
    return calculateSubtotal() - calculateDiscountAmount();
  };

  const calculateGstAmount = () => {
    return calculateAfterDiscount() * ((formData.gst || 0) / 100);
  };

  const calculateIgstAmount = () => {
    return calculateAfterDiscount() * ((formData.i_gst || 0) / 100);
  };

  const calculateTotal = () => {
    return calculateAfterDiscount() + calculateGstAmount() + calculateIgstAmount();
  };

 const handleSave = async () => {
  if (!formData.quotation_number.trim()) {
    alert('Quotation number is required');
    return;
  }

  if (items.some(item => !item.p_name.trim())) {
    alert('All items must have a product/service name');
    return;
  }

  setSaving(true);
  try {
    const totalAmount = calculateTotal();

    // Clean up terms_conditions - filter out empty terms and create proper string
    const cleanedTerms = formData.terms_conditions
      .filter(term => term && typeof term === 'string' && term.trim())
      .map(term => term.trim());

    // Prepare the update data with proper validation
    const updateData = {
      quotation_number: String(formData.quotation_number).trim(),
      total_amount: Number(totalAmount.toFixed(2)),
      discount: Number(formData.discount) || 0,
      gst: Number(formData.gst) || 0,
      i_gst: Number(formData.i_gst) || 0,
      // Store as pipe-separated string, or null if no valid terms
      terms_conditions: cleanedTerms.length > 0 ? cleanedTerms.join('|') : null,
      // Handle client_id properly - convert empty string to null
      client_id: formData.client_id && formData.client_id.trim() ? formData.client_id : null
    };

    // Remove any undefined values and ensure all values are properly serializable
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined || updateData[key] === '') {
        if (key === 'terms_conditions' || key === 'client_id') {
          updateData[key] = null; // Explicitly set to null for nullable fields
        } else {
          delete updateData[key];
        }
      }
      // Ensure numbers are not NaN
      if (typeof updateData[key] === 'number' && isNaN(updateData[key])) {
        updateData[key] = 0;
      }
    });

    console.log('Updating quote with data:', updateData);

    // Update the main quote record in quotes table
    const { error: quoteError } = await supabase
      .from('quotes')
      .update(updateData)
      .eq('id', quote.id);

    if (quoteError) {
      console.error('Quote update error:', quoteError);
      throw quoteError;
    }

    // Delete existing quote items
    const { error: deleteError } = await supabase
      .from('quote_items')
      .delete()
      .eq('quote_id', quote.id);

    if (deleteError) {
      console.error('Error deleting existing items:', deleteError);
      throw deleteError;
    }

    // Insert new/updated quote items
    const validItems = items.filter(item => item.p_name && item.p_name.trim());
    if (validItems.length > 0) {
      const itemsToInsert = validItems.map(item => {
        const quantity = Number(item.quantity) || 1;
        const unitPrice = Number(item.unit_price) || 0;
        const totalValue = Number((quantity * unitPrice).toFixed(2));

        return {
          quote_id: quote.id,
          user_id: quote.user_id,
          p_name: String(item.p_name).trim(),
          description: String(item.description || '').trim() || null, // Allow null for empty descriptions
          quantity: quantity,
          unit_price: unitPrice,
          total_value: totalValue
        };
      });

      console.log('Inserting items:', itemsToInsert);

      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(itemsToInsert);

      if (itemsError) {
        console.error('Error inserting quote items:', itemsError);
        throw itemsError;
      }
    }

    alert('Quotation updated successfully!');
    console.log('Quotation Saved');
    // Call onSave with the updated data to refresh the parent component
    if (onSave) {
      const updatedQuoteData = {
        ...quote,
        ...updateData,
        // Include the total amount in the expected format
        total_amount: totalAmount
      };
      await onSave(updatedQuoteData);
    }
    
    onClose();

  } catch (error) {
    console.error('Error saving quote:', error);
    alert('Error saving quotation: ' + (error.message || 'Unknown error'));
  }
  setSaving(false);
};

  if (!isOpen) return null;

  return (
    <div className="EditQuotePopup-overlay">
      <div className="EditQuotePopup-popup">
        <div className="EditQuotePopup-header">
          <h2 className="EditQuotePopup-title">
            <FileText size={20} />
            Edit Quotation
          </h2>
          <button className="EditQuotePopup-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="EditQuotePopup-content">
          {loading ? (
            <div className="EditQuotePopup-loading">
              <div className="EditQuotePopup-loading-spinner"></div>
              <p>Loading quotation data...</p>
            </div>
          ) : (
            <>
              {/* Basic Quote Info */}
              <div className="EditQuotePopup-section">
                <h3 className="EditQuotePopup-section-title">Quotation Information</h3>
                <div className="EditQuotePopup-form-grid">
                  <div className="EditQuotePopup-form-group">
                    <label htmlFor="quotation_number">Quotation Number</label>
                    <input
                      id="quotation_number"
                      type="text"
                      value={formData.quotation_number}
                      onChange={(e) => handleFormChange('quotation_number', e.target.value)}
                      placeholder="Enter quotation number"
                    />
                  </div>

                  <div className="EditQuotePopup-form-group">
                    <label htmlFor="issue_date">Issue Date</label>
                    <input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => handleFormChange('issue_date', e.target.value)}
                    />
                  </div>

                  <div className="EditQuotePopup-form-group">
                    <label htmlFor="client_id">Client</label>
                    <select
                      id="client_id"
                      value={formData.client_id}
                      onChange={(e) => handleFormChange('client_id', e.target.value)}
                    >
                      <option value="">Select Client</option>
                      {clientsList.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.client_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div className="EditQuotePopup-section">
                <div className="EditQuotePopup-section-header">
                  <h3 className="EditQuotePopup-section-title">Items</h3>
                  <button className="EditQuotePopup-add-item-btn" onClick={addItem}>
                    <Plus size={16} />
                    Add Item
                  </button>
                </div>
                
                <div className="EditQuotePopup-items-container">
                  {items.map((item, index) => (
                    <div key={item.id} className="EditQuotePopup-item-card">
                      <div className="EditQuotePopup-item-header">
                        <h4>Item {index + 1}</h4>
                        {items.length > 1 && (
                          <button 
                            className="EditQuotePopup-remove-item-btn"
                            onClick={() => removeItem(item.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      
                      <div className="EditQuotePopup-item-form-grid">
                        <div className="EditQuotePopup-form-group EditQuotePopup-full-width">
                          <label>Product/Service Name</label>
                          <input
                            type="text"
                            value={item.p_name}
                            onChange={(e) => handleItemChange(item.id, 'p_name', e.target.value)}
                            placeholder="Enter product or service name"
                          />
                        </div>

                        <div className="EditQuotePopup-form-group EditQuotePopup-full-width">
                          <label>Description</label>
                          <textarea
                            value={item.description}
                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                            placeholder="Enter description"
                            rows={2}
                          />
                        </div>

                        <div className="EditQuotePopup-form-group">
                          <label>Quantity</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                          />
                        </div>

                        <div className="EditQuotePopup-form-group">
                          <label>Unit Price (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(item.id, 'unit_price', e.target.value)}
                          />
                        </div>

                        <div className="EditQuotePopup-form-group">
                          <label>Total</label>
                          <div className="EditQuotePopup-calculated-value">
                            ₹{calculateItemTotal(item).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div className="EditQuotePopup-section">
                <h3 className="EditQuotePopup-section-title">Price Summary</h3>
                <div className="EditQuotePopup-price-breakdown">
                  <div className="EditQuotePopup-price-row">
                    <span>Subtotal:</span>
                    <span>₹{calculateSubtotal().toFixed(2)}</span>
                  </div>
                  
                  <div className="EditQuotePopup-form-group-inline">
                    <label htmlFor="discount">Discount (%):</label>
                    <input
                      id="discount"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => handleFormChange('discount', parseFloat(e.target.value) || 0)}
                      className="EditQuotePopup-inline-input"
                    />
                    <span>-₹{calculateDiscountAmount().toFixed(2)}</span>
                  </div>

                  <div className="EditQuotePopup-price-row">
                    <span>After Discount:</span>
                    <span>₹{calculateAfterDiscount().toFixed(2)}</span>
                  </div>

                  <div className="EditQuotePopup-form-group-inline">
                    <label htmlFor="gst">GST (%):</label>
                    <input
                      id="gst"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.gst}
                      onChange={(e) => handleFormChange('gst', parseFloat(e.target.value) || 0)}
                      className="EditQuotePopup-inline-input"
                    />
                    <span>₹{calculateGstAmount().toFixed(2)}</span>
                  </div>

                  <div className="EditQuotePopup-form-group-inline">
                    <label htmlFor="i_gst">IGST (%):</label>
                    <input
                      id="i_gst"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.i_gst}
                      onChange={(e) => handleFormChange('i_gst', parseFloat(e.target.value) || 0)}
                      className="EditQuotePopup-inline-input"
                    />
                    <span>₹{calculateIgstAmount().toFixed(2)}</span>
                  </div>

                  <div className="EditQuotePopup-price-row EditQuotePopup-total-row">
                    <span><strong>Total Amount:</strong></span>
                    <span><strong>₹{calculateTotal().toFixed(2)}</strong></span>
                    </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="EditQuotePopup-section">
                <div className="EditQuotePopup-section-header">
                  <h3 className="EditQuotePopup-section-title">Terms & Conditions</h3>
                  <button className="EditQuotePopup-add-term-btn" onClick={addTerm}>
                    <Plus size={16} />
                    Add Term
                  </button>
                </div>
                
                <div className="EditQuotePopup-terms-container">
                  {formData.terms_conditions.map((term, index) => (
                    <div key={index} className="EditQuotePopup-term-item">
                      <input
                        type="text"
                        value={term}
                        onChange={(e) => handleTermChange(index, e.target.value)}
                        placeholder={`Term ${index + 1}`}
                      />
                      {formData.terms_conditions.length > 1 && (
                        <button 
                          className="EditQuotePopup-remove-term-btn"
                          onClick={() => removeTerm(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="EditQuotePopup-footer">
          <button className="EditQuotePopup-cancel-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button 
            className="EditQuotePopup-save-btn" 
            onClick={handleSave} 
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="EditQuotePopup-btn-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditQuotePopup;